import * as React from 'react';
import { Link } from 'react-router';
import { AccountsError, resetPassword } from '../accounts.ts';
import { AUTH_LINK, AUTH_SUBMIT, AuthField, AuthNotice, AuthShell, useFragmentToken } from './AuthShell.tsx';

export const MIN_PASSWORD = 8;

/** Why a new password (and its confirmation) cannot be sent yet, or ''. */
export function passwordProblem(password: string, confirm: string) {
  if (password.length < MIN_PASSWORD) return `At least ${MIN_PASSWORD} characters.`;
  if (password !== confirm) return 'The two passwords differ.';
  return '';
}

/**
 * The page a reset link opens (`/reset-password#token=…`, ADR 0014 §10). The token is taken out of the
 * address bar at once; a new password ends every session of the account.
 */
export function ResetPasswordPage() {
  const token = useFragmentToken();
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState(token ? '' : 'This link is not valid any more.');
  const [dead, setDead] = React.useState(!token);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const p = passwordProblem(password, confirm);
    if (p) return setError(p);
    setBusy(true);
    setError('');
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      if (err instanceof AccountsError && err.status === 400) {
        setDead(true);
        setError('This link is not valid any more.');
      } else setError(err instanceof AccountsError ? err.message : 'The reset failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Set a new password" testId="reset-page">
      {done ? (
        <div className="flex flex-col gap-5">
          <AuthNotice kind="ok" testId="reset-done">
            Password changed. Sign in with your new password.
          </AuthNotice>
          <Link to="/login" className={AUTH_LINK}>
            Sign in
          </Link>
        </div>
      ) : dead ? (
        <div className="flex flex-col gap-5">
          <AuthNotice kind="error" testId="reset-error">
            {error}
          </AuthNotice>
          <Link to="/forgot-password" className={AUTH_LINK}>
            Ask for a new link
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          {error && (
            <AuthNotice kind="error" testId="reset-error">
              {error}
            </AuthNotice>
          )}
          <AuthField
            id="reset-password"
            label="New password"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            testId="reset-password"
          />
          <AuthField
            id="reset-confirm"
            label="Confirm the new password"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            testId="reset-confirm"
          />
          <button type="submit" disabled={busy} data-testid="reset-submit" className={AUTH_SUBMIT}>
            {busy ? 'Saving…' : 'Set password'}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
